/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Assembles the compact data snapshot the home-screen widgets render. Prefers
 * the single cheap backend aggregator (`GET /api/widgets/summary`); when that is
 * unavailable it degrades to per-source calls (calendar + mail) so widgets still
 * populate on older backends. Never throws — a partial/empty snapshot is always
 * returned so the caller can still publish it.
 *
 * The returned object is intentionally small and fully serializable (no Dates,
 * no functions): it is JSON-stringified into OS-shared storage by the bridge.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import { settleAll } from '../promisePolyfills';
import { apiFetch } from '../WorkSheetContext';
import { normalizeWidgetWorkday } from '../workdayApi';
import { fetchDailyTasks } from '../timeTrackingApi';
import { DAILY_TASKS_WIDGET_MAX, toWidgetDailyTasks } from '../dailyTasks';
import {
    idleTask,
    idleWorkday,
    normalizeTaskClock,
    pickAuthoritativeClocks,
    taskClockFromTimer,
    toMs,
} from './trackerState';
import calendarApi from '../calendarApi';
import { getMailStatus, listMailMessages } from '../../services/mailApi';
import { buildInsightsSection, planInsightFetch, pruneInsightExtras } from './buildInsightsSnapshot';
import { listInstanceConfigs } from './widgetInstanceConfig';
import { getVariantAllowedModes } from '../../config/appVariant';
import { WIDGET_PREFS_KEY, WIDGET_SNAPSHOT_KEY, buildWidgetDeepLink } from '../../config/widgetConfig';

/** How many upcoming events / mail items a widget lists at most. */
const MAX_EVENTS = 6;
const MAX_MAIL = 6;
/** How many open assigned tasks the snapshot carries (the aggregator's cap). */
const MAX_TASKS = 10;
/** Look-ahead window (days) for "upcoming" calendar events. */
const LOOKAHEAD_DAYS = 14;

/**
 * Segment types that mean "the technician is resting", used to derive the
 * time-tracker `state` when talking to a backend that predates contract C1 and
 * therefore never sends `timer.state`.
 * @type {string[]}
 */
const BREAK_SEGMENT_TYPES = ['BREAK', 'COFFEE_TIME'];

/** The three authoritative time-tracker states (contract C1). */
const TIMER_STATES = ['running', 'break', 'paused'];

/**
 * Flat i18n key per `labels.timeTracker.*` field (contract C7). The native
 * widgets cannot read the JS catalog, so every user-visible time-tracker string
 * is resolved here and shipped inside the snapshot.
 * @type {Record<string, string>}
 */
const TIME_TRACKER_LABEL_KEYS = {
    title: 'widget_tt_title',
    running: 'widget_tt_running',
    onBreak: 'widget_tt_on_break',
    paused: 'widget_tt_paused',
    idle: 'widget_tt_idle',
    noActiveWork: 'widget_tt_no_active_work',
    project: 'widget_tt_project',
    task: 'widget_tt_task',
    workTime: 'widget_tt_work_time',
    breakTime: 'widget_tt_break_time',
    start: 'widget_tt_start',
    pause: 'widget_tt_pause',
    resume: 'widget_tt_resume',
    breakAction: 'widget_tt_break',
    stop: 'widget_tt_stop',
    quickStart: 'widget_tt_quick_start',
    dailyTasks: 'tt_daily_title',
    newTask: 'widget_tt_new_task',
    updatedAt: 'widget_tt_updated_at',
    workday: 'workday_title',
    workdayStop: 'workday_stop_short',
    workdayRow: 'widget_tt_workday_row',
    startWorkday: 'workday_start',
    startTimer: 'widget_tt_start_timer',
    stale: 'widget_tt_stale',
};

/**
 * Hungarian literals the widgets shipped with. They are BOTH the i18n
 * `defaultValue` and the last-resort value when the catalog cannot be reached
 * at all (headless task, i18next not yet initialized), so a label can never
 * render blank or as a raw key name. Mirrors `TimeTrackerLabels` in
 * `targets/widgets/WidgetSharedData.swift`.
 * @type {Record<string, string>}
 */
const TIME_TRACKER_LABEL_FALLBACKS = {
    title: 'Időmérő',
    running: 'Fut',
    onBreak: 'Szünet',
    paused: 'Szüneteltetve',
    idle: 'Tétlen',
    noActiveWork: 'Nincs aktív munka',
    project: 'Projekt',
    task: 'Feladat',
    workTime: 'Munkaidő',
    breakTime: 'Pihenőidő',
    start: 'Indítás',
    pause: 'Szünet',
    resume: 'Folytatás',
    breakAction: 'Pihenő',
    stop: 'Leállítás',
    quickStart: 'Gyorsindítás',
    dailyTasks: 'Mai feladatok',
    newTask: 'Új feladat',
    updatedAt: 'Frissítve',
    workday: 'Munkanap',
    workdayStop: 'Lezárás',
    workdayRow: 'Munkaidő',
    startWorkday: 'Munkanap indítása',
    startTimer: 'Időmérő indítása',
    stale: 'Frissítés szükséges',
};

/**
 * Flat i18n key per `labels.mail.*` field (contract C7, mail widget). Same
 * mechanism as the time-tracker block: the native widgets cannot read the JS
 * catalog, so every user-visible mail-widget string is resolved here and
 * shipped inside the snapshot. Existing `mail_*` catalog keys are reused; only
 * the compact relative-age tokens are widget-specific.
 * @type {Record<string, string>}
 */
const MAIL_LABEL_KEYS = {
    title: 'mail_title',
    empty: 'mail_empty_title',
    notConfigured: 'mail_compose_no_account',
    setUp: 'mail_setup_cta',
    unknownSender: 'mail_unknown_sender',
    now: 'mail_widget_now',
    minuteSuffix: 'mail_widget_minute_suffix',
    hourSuffix: 'mail_widget_hour_suffix',
    daySuffix: 'mail_widget_day_suffix',
};

/**
 * Hungarian literals for the mail widget — BOTH the i18n `defaultValue` and the
 * last-resort value when the catalog cannot be reached at all (headless task,
 * i18next not yet initialized). Mirrors `MailLabels` in
 * `targets/widgets/WidgetSharedData.swift` and `MAIL_LABEL_FALLBACK` in
 * `app/widgets/WidgetViews.js`.
 * @type {Record<string, string>}
 */
const MAIL_LABEL_FALLBACKS = {
    title: 'Levelezés',
    empty: 'Nincs üzenet',
    notConfigured: 'Nincs beállított levelezőfiók.',
    setUp: 'Postafiók beállítása',
    unknownSender: 'Ismeretlen feladó',
    now: 'most',
    minuteSuffix: 'p',
    hourSuffix: 'ó',
    daySuffix: 'n',
};

/**
 * Flat i18n key per `labels.widgets.*` field (contract C7, shared block). One
 * localized set for the remaining widget faces (calendar, stats, pending
 * orders, quick launch, meeting recorder), resolved here because the native
 * widgets cannot read the JS catalog. Existing catalog keys are reused where
 * the exact string already ships (`calendar_title`, `statistics`,
 * `widget_tt_quick_start`); only the widget-specific captions are new.
 * @type {Record<string, string>}
 */
const WIDGET_LABEL_KEYS = {
    calendarTitle: 'calendar_title',
    calendarEmpty: 'widget_cal_empty',
    statsTitle: 'statistics',
    statsEmpty: 'widget_stats_empty',
    ordersTitle: 'widget_orders_title',
    ordersEmpty: 'widget_orders_empty',
    quickLaunchTitle: 'widget_tt_quick_start',
    quickLaunchEmpty: 'widget_ql_empty',
    meetingTitle: 'widget_meeting_title',
    meetingRecord: 'widget_meeting_record',
    meetingHint: 'widget_meeting_hint',
    tasksTitle: 'widget_tasks_title',
    tasksCountSuffix: 'widget_tasks_count_suffix',
    allDay: 'widget_cal_all_day',
    today: 'widget_cal_today',
    allClear: 'widget_cal_all_clear',
    // Team Chat widget.
    chatTitle: 'widget_chat_title',
    chatEmpty: 'widget_chat_empty',
    chatUnavailable: 'widget_chat_unavailable',
    chatUnread: 'widget_chat_unread',
    // Meetings widget.
    meetingsTitle: 'widget_meetings_title',
    meetingsEmpty: 'widget_meetings_empty',
    meetingsUnavailable: 'widget_meetings_unavailable',
    meetingsLive: 'widget_meetings_live',
    meetingsJoin: 'widget_meetings_join',
    meetingsUpcoming: 'widget_meetings_upcoming',
    meetingsPeople: 'widget_meetings_people',
    // Calls widget.
    callsTitle: 'widget_calls_title',
    callsMissedToday: 'widget_calls_missed_today',
    callsEmpty: 'widget_calls_empty',
    callsDial: 'widget_calls_dial',
    callsHistory: 'widget_calls_history',
    callsUnknown: 'widget_calls_unknown',
    // My tasks widget.
    myTasksTitle: 'widget_mytasks_title',
    tasksOverdue: 'widget_tasks_overdue',
    tasksDueToday: 'widget_tasks_due_today',
    tasksOpen: 'widget_tasks_open',
    tasksEmpty: 'widget_tasks_empty',
    // Shared signed-out / not-yet-synced card of every widget.
    signedOutTitle: 'widget_signed_out_title',
    signedOutBody: 'widget_signed_out_body',
    signedOutCta: 'widget_signed_out_cta',
    // Section eyebrow of the calendar widget + the "view all" tail of every list's "+N" overflow link.
    eventsTitle: 'widget_events_title',
    viewAll: 'widget_view_all',
};

/**
 * Hungarian literals for the shared widget block — BOTH the i18n `defaultValue`
 * and the last-resort value when the catalog cannot be reached at all
 * (headless task, i18next not yet initialized). Mirrors
 * `WIDGET_LABEL_FALLBACK` in `app/widgets/WidgetViews.js`.
 * @type {Record<string, string>}
 */
const WIDGET_LABEL_FALLBACKS = {
    calendarTitle: 'Naptár',
    calendarEmpty: 'Nincs közelgő esemény',
    statsTitle: 'Statisztika',
    statsEmpty: 'Még nincs megjeleníthető adat',
    ordersTitle: 'Függő rendelések',
    ordersEmpty: 'Nincs függő rendelés',
    quickLaunchTitle: 'Gyorsindítás',
    quickLaunchEmpty: 'Nincs elérhető modul',
    meetingTitle: 'Megbeszélés rögzítése',
    meetingRecord: 'Rögzítés',
    meetingHint: 'Koppints a rögzítéshez és a beállításokhoz',
    tasksTitle: 'Feladatok',
    tasksCountSuffix: 'feladat',
    allDay: 'Egész nap',
    today: 'Ma',
    allClear: 'Nincs közelgő esemény vagy feladat',
    chatTitle: 'Csapatchat',
    chatEmpty: 'Nincs új üzenet',
    chatUnavailable: 'A csapatchat ebben az üzletben nem érhető el',
    chatUnread: 'olvasatlan',
    meetingsTitle: 'Megbeszélések',
    meetingsEmpty: 'Nincs tervezett megbeszélés',
    meetingsUnavailable: 'A megbeszélések ebben az üzletben nem érhetők el',
    meetingsLive: 'Élő',
    meetingsJoin: 'Csatlakozás',
    meetingsUpcoming: 'Következő',
    meetingsPeople: 'fő',
    callsTitle: 'Hívások',
    callsMissedToday: 'Nem fogadott ma',
    callsEmpty: 'Nincs friss hívás',
    callsDial: 'Tárcsázó',
    callsHistory: 'Híváslista',
    callsUnknown: 'Ismeretlen hívó',
    myTasksTitle: 'Feladataim',
    tasksOverdue: 'Lejárt',
    tasksDueToday: 'Ma esedékes',
    tasksOpen: 'Nyitott',
    tasksEmpty: 'Nincs nyitott feladat',
    signedOutTitle: 'Jelentkezz be',
    signedOutBody: 'Nyisd meg a Happy alkalmazást a bejelentkezéshez és a szinkronizáláshoz',
    signedOutCta: 'Megnyitás',
    eventsTitle: 'Események',
    viewAll: 'Összes',
};

/** How many Team Chat conversations / meetings / recent calls the snapshot carries. */
const MAX_CHAT_CHANNELS = 6;
const MAX_MEETINGS = 5;
const MAX_RECENT_CALLS = 6;

/** Human labels + deep-link targets for the quick-launch tiles, per app mode. */
const MODE_QUICK_TILES = {
    restaurant: { label: 'POS', icon: 'point-of-sale', target: 'home' },
    sales: { label: 'Sales', icon: 'work-outline', target: 'home' },
    manager: { label: 'Management', icon: 'insights', target: 'stats' },
    service: { label: 'Service', icon: 'build', target: 'home' },
    stock: { label: 'Warehouse', icon: 'inventory', target: 'home' },
};

/**
 * Normalize a calendar event doc to the minimal widget shape. `end`/`allDay`
 * are additive (aggregator contract): older backends never send them, so they
 * degrade to `null`/`false` and old snapshots keep rendering unchanged.
 * @param {object} event
 * @returns {{ id: string, title: string, start: string|null, end: string|null, allDay: boolean, location: string }}
 */
const toWidgetEvent = (event) => ({
    id: String(event?._id || event?.id || ''),
    title: String(event?.title || event?.summary || event?.name || ''),
    start: event?.start || event?.startDate || event?.startsAt || null,
    end: event?.end || event?.endDate || event?.endsAt || null,
    allDay: Boolean(event?.allDay),
    location: String(event?.location || event?.place || ''),
});

/**
 * Normalize an aggregator task row (the caller's open assigned tasks) to the
 * minimal widget shape. Every field is guarded — a partial row can never break
 * the native render.
 * @param {object} task Raw `summary.tasks[]` entry.
 * @returns {{ id: string, title: string, dueDate: string|null, status: string|null, projectId: string|null, projectTitle: string|null }}
 */
const toWidgetTask = (task) => ({
    id: String(task?.id || task?._id || ''),
    title: String(task?.title || task?.name || ''),
    dueDate: task?.dueDate || null,
    status: task?.status ? String(task.status) : null,
    projectId: task?.projectId ? String(task.projectId) : null,
    projectTitle: task?.projectTitle ? String(task.projectTitle) : null,
});

/**
 * Extract a human display label from a mail `from` field. Backend `MailMessage.from`
 * is an **array** of `{ name, email }` sub-docs; the aggregator already flattens it
 * to a string, but the per-source fallback returns the raw array. Both are handled
 * here so the widget never renders `[object Object]`.
 * @param {(Array|object|string)} from
 * @returns {string}
 */
const pickMailFrom = (from) => {
    const first = Array.isArray(from) ? from[0] : from;

    if (!first) {
        return '';
    }
    if (typeof first === 'string') {
        return first;
    }

    return String(first.name || first.email || first.address || first.text || '');
};

/**
 * Normalize a mail message doc to the minimal widget shape.
 * Exported for the mail-only widget feed (`normalizeMailFeed`/`fetchMailSection`).
 * @param {object} message
 * @returns {{ id: string, from: string, subject: string, date: string|null, unread: boolean, threadKey: string|null }}
 */
export const toWidgetMail = (message) => ({
    id: String(message?._id || message?.id || ''),
    from: pickMailFrom(message?.from),
    subject: String(message?.subject || ''),
    date: message?.date || message?.receivedAt || message?.createdAt || null,
    unread: !(message?.seen || message?.read || message?.isRead),
    // Carried so a widget tap can deep-link straight to the thread.
    threadKey: message?.threadKey ? String(message.threadKey) : null,
});

/**
 * Read the user's saved widget preferences (which sections to feature). Falls
 * back to sensible defaults when unset.
 * @returns {Promise<{ sections: string[] }>}
 */
const readPrefs = async () => {
    try {
        const raw = await AsyncStorage.getItem(WIDGET_PREFS_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const sections = Array.isArray(parsed?.sections) ? parsed.sections : null;
        // Additive: app-level insight widget defaults (`insights[<KindId>]`).
        const insights = parsed?.insights && typeof parsed.insights === 'object' ? parsed.insights : {};

        return { sections: sections || ['calendar', 'stats', 'mail', 'quickLaunch'], insights };
    } catch (_error) {
        return { sections: ['calendar', 'stats', 'mail', 'quickLaunch'], insights: {} };
    }
};

/**
 * Read the active restaurant id (AsyncStorage `restaurant`) so interactive
 * work-order timer calls can send the required `x-restaurant-id` scope header.
 * @returns {Promise<string|null>}
 */
const readRestaurantId = async () => {
    try {
        const raw = await AsyncStorage.getItem('restaurant');
        const parsed = raw ? JSON.parse(raw) : null;

        return parsed?._id ? String(parsed._id) : null;
    } catch (_error) {
        return null;
    }
};

/**
 * Coerce an optional backend string to a trimmed value, or `null` when it is
 * absent/blank. Keeps the snapshot free of `""`/`undefined`, both of which the
 * native widgets would otherwise render as an empty line.
 * @param {*} value
 * @returns {string|null}
 */
const optionalText = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const text = String(value).trim();

    return text ? text : null;
};

/**
 * Resolve the authoritative widget state (contract C1). Prefers the backend's
 * own `state`; on an older aggregator that does not send it, derives it from the
 * live segment type (BREAK/COFFEE_TIME → `break`, anything else → `running`).
 * A pre-C1 backend never returns a paused timer at all, so `paused` can only
 * ever come from the explicit field.
 * @param {object} raw Raw `summary.timer`.
 * @returns {'running'|'break'|'paused'}
 */
const resolveTimerState = (raw) => {
    const explicit = String(raw?.state || '').trim();

    if (TIMER_STATES.includes(explicit)) {
        return explicit;
    }

    return BREAK_SEGMENT_TYPES.includes(String(raw?.segmentType || '').toUpperCase())
        ? 'break'
        : 'running';
};

/**
 * Normalize the aggregator's running/paused timer to the contract C1 widget
 * shape. The five original fields keep their exact meaning; every additive one
 * degrades gracefully against an older backend that never sends it.
 * @param {object} raw Raw `summary.timer`.
 * @returns {object|null} The C1 timer object, or `null` when nothing is tracked.
 */
const toWidgetTimer = (raw) => {
    if (!raw || !raw.id) {
        return null;
    }

    const kind = raw.kind === 'project' ? 'project' : 'workOrder';

    return {
        kind,
        id: String(raw.id),
        // Back-compat headline ("#WO-123" / project title) — unchanged.
        title: String(raw.title || ''),
        // NEW (C1): the project / work-order eyebrow above the task line.
        projectTitle: optionalText(raw.projectTitle),
        taskId: raw.taskId ? String(raw.taskId) : null,
        // NEW (C1): the task actually being tracked — the widget's primary line.
        taskTitle: optionalText(raw.taskTitle),
        customerName: optionalText(raw.customerName),
        // Start of the CURRENT segment; null while paused.
        startedAt: raw.startedAt || null,
        state: resolveTimerState(raw),
        // Work-order break/work totals + live segment type (null for projects),
        // so the widget can mirror the admin task-detail time tracker card.
        segmentType: raw.segmentType || null,
        workSeconds: raw.workSeconds == null ? null : Number(raw.workSeconds) || 0,
        breakSeconds: raw.breakSeconds == null ? null : Number(raw.breakSeconds) || 0,
        // NEW (C1/P9): the PAUSED row's own settled duration. The paused clock
        // renders this instead of `workSeconds`, which is null for projects and
        // the work order's lifetime total. Null unless the backend says paused.
        pausedElapsedSeconds:
            raw.pausedElapsedSeconds == null ? null : Number(raw.pausedElapsedSeconds) || 0,
        // Capability flags drive the action row. BREAK is a real tracked segment
        // type, so it only exists on work orders; PAUSE exists on both kinds.
        canBreak: raw.canBreak == null ? kind === 'workOrder' : Boolean(raw.canBreak),
        canPause: raw.canPause == null ? true : Boolean(raw.canPause),
    };
};

/**
 * Normalize a quick-start trackable to the contract C2 widget shape.
 * @param {object} item Raw `summary.trackables[]` entry.
 * @returns {{ kind: string, id: string, title: string, subtitle: string|null, taskId: string|null, taskTitle: string|null }}
 */
const toWidgetTrackable = (item) => ({
    kind: item?.kind === 'project' ? 'project' : 'workOrder',
    id: String(item?.id || ''),
    title: String(item?.title || ''),
    // NEW (C2): customer or project name — the row's small second line.
    subtitle: optionalText(item?.subtitle),
    taskId: item?.taskId ? String(item.taskId) : null,
    taskTitle: optionalText(item?.taskTitle),
});

/**
 * Normalize the aggregator's Team Chat section. Key names match the backend
 * row exactly — the iOS extension also merges the RAW `/widgets/summary`
 * body over the snapshot, so both shapes must decode identically.
 * `available` is tri-state: only an explicit `false` renders the
 * "not available in this store" state; an older backend that never sends the
 * section yields `null` (plain empty state).
 * @param {object|undefined} raw `summary.chat`.
 * @returns {{ available: boolean|null, unreadTotal: number, mentionTotal: number, channels: Array<object> }}
 */
export const toWidgetChat = (raw) => ({
    available: typeof raw?.available === 'boolean' ? raw.available : null,
    unreadTotal: Math.max(0, Number(raw?.unreadTotal) || 0),
    mentionTotal: Math.max(0, Number(raw?.mentionTotal) || 0),
    channels: (Array.isArray(raw?.channels) ? raw.channels : [])
        .map((channel) => ({
            id: String(channel?.id || channel?._id || ''),
            name: String(channel?.name || ''),
            type: String(channel?.type || 'public'),
            unread: Math.max(0, Number(channel?.unread) || 0),
            mentions: Math.max(0, Number(channel?.mentions) || 0),
            preview: String(channel?.preview || ''),
            sender: String(channel?.sender || ''),
            at: channel?.at || null,
        }))
        .filter((channel) => channel.id)
        .slice(0, MAX_CHAT_CHANNELS),
});

/**
 * Normalize one meeting row (live or scheduled) — backend key names kept.
 * @param {object} meeting Raw `summary.meetings.live[]` / `.upcoming[]` row.
 * @returns {{ id: string, title: string, status: string, start: string|null, end: string|null, participants: number, channelId: string|null, channelName: string }}
 */
const toWidgetMeeting = (meeting) => ({
    id: String(meeting?.id || meeting?._id || ''),
    title: String(meeting?.title || ''),
    status: meeting?.status === 'live' ? 'live' : 'scheduled',
    start: meeting?.start || null,
    end: meeting?.end || null,
    participants: Math.max(0, Number(meeting?.participants) || 0),
    channelId: meeting?.channelId ? String(meeting.channelId) : null,
    channelName: String(meeting?.channelName || ''),
});

/**
 * Normalize the aggregator's meetings section (tri-state `available`, see
 * {@link toWidgetChat}).
 * @param {object|undefined} raw `summary.meetings`.
 * @returns {{ available: boolean|null, live: Array<object>, upcoming: Array<object> }}
 */
export const toWidgetMeetings = (raw) => ({
    available: typeof raw?.available === 'boolean' ? raw.available : null,
    live: (Array.isArray(raw?.live) ? raw.live : [])
        .map(toWidgetMeeting)
        .filter((meeting) => meeting.id)
        .slice(0, MAX_MEETINGS),
    upcoming: (Array.isArray(raw?.upcoming) ? raw.upcoming : [])
        .map(toWidgetMeeting)
        .filter((meeting) => meeting.id)
        .slice(0, MAX_MEETINGS),
});

/**
 * Normalize the aggregator's VoIP calls section. `missedToday` is additive —
 * an older backend only sends the all-time `missed`, which is then left out
 * of the headline (`null`) rather than shown as "today".
 * @param {object|undefined} raw `summary.calls`.
 * @returns {{ missed: number, missedToday: number|null, recent: Array<{ phone: string, name: string, direction: string, missed: boolean, at: string|null }> }}
 */
export const toWidgetCalls = (raw) => ({
    missed: Math.max(0, Number(raw?.missed) || 0),
    missedToday: raw?.missedToday == null ? null : Math.max(0, Number(raw.missedToday) || 0),
    recent: (Array.isArray(raw?.recent) ? raw.recent : [])
        .map((call) => ({
            phone: String(call?.phone || ''),
            name: String(call?.name || ''),
            direction: call?.direction === 'outgoing' ? 'outgoing' : 'incoming',
            missed: Boolean(call?.missed),
            at: call?.at || null,
        }))
        .filter((call) => call.phone || call.name)
        .slice(0, MAX_RECENT_CALLS),
});

/**
 * Resolve one localized label set the NATIVE widgets render (contract C7). The
 * Swift widget cannot read the JS i18n catalog and the Android widget runs in a
 * headless task, so the app resolves the strings once here.
 *
 * i18n is `require`d lazily (the same pattern as
 * `app/screens/ManagementUsersScreen.js#localizedRoleLabel`) and fully guarded:
 * this helper also runs from a background task, where the catalog may not be
 * initialized yet. Every label falls back to its Hungarian literal, so a missing
 * key can never render blank or as a raw catalog key.
 *
 * @param {Record<string, string>} keys Field name → flat i18n catalog key.
 * @param {Record<string, string>} fallbacks Field name → Hungarian literal.
 * @returns {Record<string, string>} One resolved string per label field.
 */
const resolveLabelSet = (keys, fallbacks) => {
    let translator = null;

    try {
        // eslint-disable-next-line global-require
        translator = require('../../../i18n').default;
    } catch (_error) {
        translator = null;
    }

    return Object.keys(fallbacks).reduce((labels, name) => {
        const key = keys[name];
        const fallback = fallbacks[name];
        let value = fallback;

        try {
            const resolved = translator?.t?.(key, { defaultValue: fallback });
            if (typeof resolved === 'string' && resolved.trim() && resolved !== key) {
                value = resolved;
            }
        } catch (_error) {
            // Catalog unavailable — keep the Hungarian literal.
        }

        labels[name] = value;

        return labels;
    }, {});
};

/**
 * Resolved time-tracker labels (contract C7) — see {@link resolveLabelSet}.
 * @returns {Record<string, string>}
 */
const buildTimeTrackerLabels = () => resolveLabelSet(TIME_TRACKER_LABEL_KEYS, TIME_TRACKER_LABEL_FALLBACKS);

/**
 * Resolved mail-widget labels (contract C7, mail block). Exported so the
 * light mail-only refresh path (`widgetRefresh.refreshMailWidgetData`) can
 * republish fresh labels without rebuilding the whole snapshot.
 * @returns {Record<string, string>}
 */
export const buildMailLabels = () => resolveLabelSet(MAIL_LABEL_KEYS, MAIL_LABEL_FALLBACKS);

/**
 * Resolved shared widget labels (contract C7, `labels.widgets` block): the
 * localized captions of every non-timer, non-mail widget face — see
 * {@link resolveLabelSet}.
 * @returns {Record<string, string>}
 */
export const buildCommonWidgetLabels = () => resolveLabelSet(WIDGET_LABEL_KEYS, WIDGET_LABEL_FALLBACKS);

/** Build the quick-launch tiles from the variant's allowed app modes. */
const buildQuickModules = () => {
    const modes = Array.isArray(getVariantAllowedModes()) ? getVariantAllowedModes() : [];

    return modes
        .map((mode) => MODE_QUICK_TILES[mode])
        .filter(Boolean)
        .map((tile) => ({ ...tile, url: buildWidgetDeepLink(tile.target) }));
};

/**
 * Fetch the compact summary from the backend aggregator. Returns `null` when the
 * endpoint is missing (older backend) or errors, so the caller falls back.
 *
 * `/widgets/summary` resolves its store from `x-restaurant-id` and otherwise
 * falls back to the caller's PRIMARY restaurant, so the active scope must ride
 * along or a multi-store user who picked store B silently gets store A's
 * calendar/tasks (which is exactly "the widget shows nothing" on store B).
 * `skipDedup` is mandatory alongside it: `apiFetch` keys its in-flight GET
 * dedupe on the PATH ALONE, so without it this call and a concurrent
 * differently-scoped `/widgets/summary` request (the time tracker screen's
 * `fetchTrackerState`) would collapse into whichever fired first and one of
 * them would silently receive the wrong store's summary.
 *
 * @param {string|null} restaurantId Active restaurant scope id, when known.
 * @returns {Promise<object|null>}
 */
const fetchSummary = async (restaurantId) => {
    try {
        const res = await apiFetch('/widgets/summary', {
            headers: restaurantId ? { 'x-restaurant-id': String(restaurantId) } : {},
            skipDedup: true,
        });
        // Endpoint present and well-formed → use directly.
        if (res && (res.calendar || res.mail || res.stats || res.tasks)) {
            return res;
        }

        return null;
    } catch (_error) {
        return null;
    }
};

/**
 * Per-source fallback used when the aggregator is unavailable. Each source is
 * best-effort and independently guarded. `calendarOk` tells the caller whether
 * the calendar source itself SUCCEEDED — an empty list from a fulfilled fetch
 * is genuine, while a rejected fetch must not wipe a previously good snapshot.
 * @returns {Promise<{ calendar: object[], calendarOk: boolean, mail: object, stats: object }>}
 */
const fetchPerSource = async () => {
    const now = new Date();
    const end = new Date(now.getTime() + LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000);

    const [eventsResult, mailStatusResult, mailListResult] = await settleAll([
        calendarApi.fetchMyEvents({ start: now.toISOString(), end: end.toISOString() }),
        getMailStatus(),
        // INBOX only, like the app inbox: an unscoped list also surfaces trash / spam / archive copies.
        listMailMessages({ folder: 'INBOX', unread: true, limit: MAX_MAIL, sort: '-date' }),
    ]);

    const events = eventsResult.status === 'fulfilled' && Array.isArray(eventsResult.value)
        ? eventsResult.value
        : [];
    const mailStatus = mailStatusResult.status === 'fulfilled' ? mailStatusResult.value : null;
    const mailDocs = mailListResult.status === 'fulfilled'
        ? (Array.isArray(mailListResult.value?.docs)
            ? mailListResult.value.docs
            : (Array.isArray(mailListResult.value) ? mailListResult.value : []))
        : [];

    return {
        calendar: events,
        calendarOk: eventsResult.status === 'fulfilled',
        mail: {
            unread: Number(mailStatus?.unread || mailStatus?.unreadCount || mailDocs.length || 0),
            configured: typeof mailStatus?.configured === 'boolean' ? mailStatus.configured : null,
            items: mailDocs,
        },
        stats: null,
    };
};

/**
 * Read + parse the LAST published snapshot (the exact JSON `widgetBridge`
 * mirrored to OS storage). Used to carry a previously good calendar/tasks
 * section over a transient total fetch failure, so a flaky network or an
 * expiring token can never blank an already-populated home-screen widget.
 * @returns {Promise<object|null>} Prior snapshot, or `null` when absent/corrupt.
 */
const readPriorSnapshot = async () => {
    try {
        const raw = await AsyncStorage.getItem(WIDGET_SNAPSHOT_KEY);

        return raw ? JSON.parse(raw) : null;
    } catch (_error) {
        return null;
    }
};

/* global setTimeout, clearTimeout */

/** Max time the insights fetch may delay the snapshot (cold backend cache can take ~6 s). */
export const INSIGHTS_TIMEOUT_MS = 3500;

/** In-flight deferred insights completion (guards against overlapping runs). */
let deferredInsightsRun = null;

/**
 * Merge a late insights section into the latest STORED snapshot and republish it.
 * Skips when the stored snapshot already holds newer insights or another store.
 * Never throws.
 * @param {object} section Completed insights section.
 * @returns {Promise<void>}
 */
const publishDeferredInsights = async (section) => {
    try {
        const latest = await readPriorSnapshot();
        if (!latest || !section) {
            return;
        }
        const stored = latest.insights;
        if (stored?.generatedAt && section.generatedAt && stored.generatedAt >= section.generatedAt) {
            return;
        }
        if (stored?.restaurantId && section.restaurantId && stored.restaurantId !== section.restaurantId) {
            return;
        }
        // Lazy require: keeps the native bridge out of this module's import graph.
        // eslint-disable-next-line global-require
        const { writeWidgetSnapshot } = require('./widgetBridge');
        await writeWidgetSnapshot({ ...latest, insights: section });
    } catch (_error) {
        // Best effort — the next scheduled refresh repaints anyway.
    }
};

/**
 * Resolve the insights section without letting a slow call delay the snapshot.
 * On timeout the prior snapshot's insights are carried over (unrequested extras
 * pruned) and ONE background completion republishes the fresh data later.
 * @param {{ restaurantId: string|null, prefs: object }} args
 * @returns {Promise<object|null>}
 */
const resolveInsights = async ({ restaurantId, prefs }) => {
    let prior = null;
    try {
        const [instances, priorSnapshot] = await Promise.all([listInstanceConfigs(), readPriorSnapshot()]);
        prior = priorSnapshot;
        const work = buildInsightsSection({ restaurantId, prefs, instances, prior }).catch(() => null);
        let timer = null;
        const timedOut = Symbol('insights-timeout');
        const timeout = new Promise((resolve) => {
            timer = setTimeout(() => resolve(timedOut), INSIGHTS_TIMEOUT_MS);
        });
        const first = await Promise.race([work, timeout]);
        clearTimeout(timer);
        if (first !== timedOut) {
            return first || prior?.insights || null;
        }
        if (!deferredInsightsRun) {
            deferredInsightsRun = work
                .then(publishDeferredInsights)
                .catch(() => {})
                .then(() => {
                    deferredInsightsRun = null;
                });
        }
        const carried = prior?.insights || null;

        return carried
            ? { ...carried, extras: pruneInsightExtras(carried.extras, planInsightFetch(prefs?.insights, instances).extras) }
            : null;
    } catch (_error) {
        // Stale-guard: keep the last good insights section.
        return prior?.insights || (await readPriorSnapshot())?.insights || null;
    }
};

/**
 * Normalize an externally supplied mail feed to the snapshot's `mail` section.
 * Accepts either the canonical `{ unread, items }` shape or the compact
 * `{ unreadCount, messages: [{ id, threadKey, fromName, subject, receivedAt,
 * seen }] }` shape the mail client hands over, so callers never have to know
 * the snapshot internals.
 * @param {object} feed Raw mail feed.
 * @returns {{ unread: number, configured: boolean|null, items: Array<object> }|null}
 *   The normalized mail section, or `null` on an unusable feed.
 */
export const normalizeMailFeed = (feed) => {
    if (!feed || typeof feed !== 'object') {
        return null;
    }

    const configured = typeof feed.configured === 'boolean' ? feed.configured : null;
    if (Array.isArray(feed.items)) {
        return {
            unread: Number(feed.unread || 0),
            configured,
            items: feed.items.map(toWidgetMail).filter((item) => item.id).slice(0, MAX_MAIL),
        };
    }

    const messages = Array.isArray(feed.messages) ? feed.messages : [];

    return {
        unread: Number(feed.unreadCount || 0),
        configured,
        items: messages
            .map((message) => toWidgetMail({
                _id: message?.id,
                threadKey: message?.threadKey,
                from: message?.fromName,
                subject: message?.subject,
                date: message?.receivedAt,
                seen: message?.seen,
            }))
            .filter((item) => item.id)
            .slice(0, MAX_MAIL),
    };
};

/**
 * Fetch ONLY the mail section — the light path behind
 * `widgetRefresh.refreshMailWidgetData`. Unlike the per-source snapshot
 * fallback (unread-only), this lists the LATEST inbox messages (read + unread,
 * newest first) so the widget mirrors the inbox exactly as the app shows it.
 * Best-effort and independently guarded — resolves `null` on total failure.
 * @returns {Promise<{ unread: number, configured: boolean|null, items: Array<object> }|null>}
 */
export const fetchMailSection = async () => {
    try {
        const [statusResult, listResult] = await settleAll([
            getMailStatus(),
            listMailMessages({ folder: 'INBOX', limit: MAX_MAIL, sort: '-date' }),
        ]);
        const status = statusResult.status === 'fulfilled' ? statusResult.value : null;
        const docs = listResult.status === 'fulfilled'
            ? (Array.isArray(listResult.value?.docs)
                ? listResult.value.docs
                : (Array.isArray(listResult.value) ? listResult.value : []))
            : [];
        if (!status && docs.length === 0) {
            // Both sources failed — keep the last published section in place.
            return null;
        }

        return {
            // Backend `getMailboxStatus` shape (`inbox.unreadIncomingCount`) with
            // the legacy short keys kept as fallbacks — same as MailContext.
            unread: Number(
                status?.inbox?.unreadIncomingCount
                    ?? status?.inbox?.unreadCount
                    ?? status?.unread
                    ?? status?.unreadCount
                    ?? 0
            ),
            configured: typeof status?.configured === 'boolean' ? status.configured : null,
            items: docs.map(toWidgetMail).filter((item) => item.id).slice(0, MAX_MAIL),
        };
    } catch (_error) {
        return null;
    }
};

/**
 * Build the data snapshot (without rendered chart images — those are injected by
 * `widgetRefresh` after an off-screen capture). Always resolves.
 *
 * @returns {Promise<{
 *   generatedAt: string,
 *   sections: string[],
 *   calendar: Array<{id,title,start,end,allDay,location}>,
 *   tasks: Array<{id,title,dueDate,status,projectId,projectTitle}>,
 *   mail: { unread: number, configured: boolean|null, items: Array<{id,from,subject,date,unread,threadKey}> },
 *   stats: { kpis: Array<{label,value,delta}>, series: number[], chartImageLight: string|null, chartImageDark: string|null },
 *   timer: object|null,
 *   orders: { pending: Array<{id,code,customer,total,createdAt}> },
 *   trackables: Array<{kind,id,title,subtitle,taskId,taskTitle}>,
 *   dailyTasks?: Array<{id,taskId,projectId,title,projectTitle,bucket}> (omitted when the backend lacks `listDailyTasks`),
 *   chat: { available: boolean|null, unreadTotal: number, mentionTotal: number, channels: Array<{id,name,type,unread,mentions,preview,sender,at}> },
 *   meetings: { available: boolean|null, live: Array<object>, upcoming: Array<object> },
 *   calls: { missed: number, missedToday: number|null, recent: Array<{phone,name,direction,missed,at}> },
 *   quickModules: Array<{label,icon,target,url}>,
 *   insights: object|null (see buildInsightsSnapshot.js),
 *   labels: { timeTracker: Record<string, string>, mail: Record<string, string>, widgets: Record<string, string> }
 * }>}
 */
export const buildWidgetSnapshot = async () => {
    const prefs = await readPrefs();
    const restaurantId = await readRestaurantId();
    // Insight widgets fetch in parallel with the summary and can never break it.
    // "Today's tasks" ride along the same way: fail-soft, never blocks the snapshot.
    const [aggregator, insights, daily] = await Promise.all([
        fetchSummary(restaurantId),
        resolveInsights({ restaurantId, prefs }),
        fetchDailyTasks(restaurantId ? { restaurantId } : undefined),
    ]);
    const summary = aggregator || (await fetchPerSource());

    let calendar = (Array.isArray(summary?.calendar) ? summary.calendar : [])
        .map(toWidgetEvent)
        .filter((event) => event.id && event.start)
        .sort((a, b) => new Date(a.start) - new Date(b.start))
        .slice(0, MAX_EVENTS);

    // NEW (additive): the caller's open assigned tasks — aggregator-only; the
    // degraded per-source fallback stays events-only, and an older backend that
    // never sends `tasks` simply yields `[]` (old snapshots keep rendering).
    let tasks = (Array.isArray(aggregator?.tasks) ? aggregator.tasks : [])
        .map(toWidgetTask)
        .filter((task) => task.id)
        .slice(0, MAX_TASKS);

    // Stale-guard: when the aggregator is unreachable AND the fallback calendar
    // source itself failed, this pass learned NOTHING new about events/tasks —
    // carry the previously published sections over instead of wiping a good
    // widget with an empty one (transient offline / token-refresh windows).
    if (!aggregator && summary?.calendarOk === false) {
        const prior = await readPriorSnapshot();
        if (calendar.length === 0 && Array.isArray(prior?.calendar) && prior.calendar.length > 0) {
            calendar = prior.calendar.slice(0, MAX_EVENTS);
        }
        if (Array.isArray(prior?.tasks) && prior.tasks.length > 0) {
            tasks = prior.tasks.slice(0, MAX_TASKS);
        }
    }

    const mailItems = (Array.isArray(summary?.mail?.items) ? summary.mail.items : [])
        .map(toWidgetMail)
        .slice(0, MAX_MAIL);

    const kpis = Array.isArray(summary?.stats?.kpis)
        ? summary.stats.kpis
            .filter((kpi) => kpi && kpi.label !== undefined)
            .map((kpi) => ({
                label: String(kpi.label),
                value: kpi.value ?? kpi.amount ?? 0,
                delta: kpi.delta ?? null,
            }))
        : [];
    const series = Array.isArray(summary?.stats?.series)
        ? summary.stats.series.map((point) => Number(point) || 0)
        : [];

    // Interactive sections (from the aggregator only): the running timer, pending
    // orders, and quick-start trackables. The restaurant id rides along so the
    // native interactive intents can send `x-restaurant-id` on work-order calls.
    const timer = toWidgetTimer(summary?.timer);
    const pendingOrders = (Array.isArray(summary?.orders?.pending) ? summary.orders.pending : [])
        .map((order) => ({
            id: String(order.id || order._id || ''),
            code: String(order.code || ''),
            customer: String(order.customer || ''),
            total: Number(order.total || 0),
            createdAt: order.createdAt || null,
        }))
        .filter((order) => order.id);
    const trackables = (Array.isArray(summary?.trackables) ? summary.trackables : [])
        .map(toWidgetTrackable)
        .filter((item) => item.id);

    // The two tracker clocks, explicit and measured at the SERVER's `generatedAt` (`clocksAt`): the workday
    // ({status, startedAt, pausedAt, breakSec, workedSecAtSnapshot, ...}) and the task time tracking
    // ({status, name, startedAt, elapsedSecAtSnapshot, ...}). An older backend that sends neither yields idle workday and a
    // task derived from the C1 `timer`; native widgets that do not know the fields simply ignore them.
    const serverAtMs = toMs(aggregator?.generatedAt);
    let clocksAt = aggregator ? new Date(serverAtMs ?? Date.now()).toISOString() : null;
    let workday = normalizeWidgetWorkday(aggregator?.workday) || idleWorkday();
    let task = normalizeTaskClock(aggregator?.task) || taskClockFromTimer(timer, serverAtMs ?? Date.now());
    let optimisticAt;
    let chat = toWidgetChat(aggregator?.chat);
    let meetings = toWidgetMeetings(aggregator?.meetings);
    let calls = toWidgetCalls(aggregator?.calls);
    // Team Chat / meetings / calls are aggregator-only. Without the aggregator this pass learned nothing about them, so
    // the last published sections are carried over instead of blanking a populated widget (same stale-guard as the
    // calendar above). The clocks are carried too, but WITH their original `clocksAt`: the widgets then see how old they
    // are and freeze a "running" clock past the stale threshold instead of counting on faith.
    if (!aggregator) {
        const prior = await readPriorSnapshot();
        if (prior?.workday || prior?.task) {
            workday = normalizeWidgetWorkday(prior.workday) || idleWorkday();
            task = normalizeTaskClock(prior.task) || idleTask();
            clocksAt = prior.clocksAt || prior.generatedAt || null;
            optimisticAt = prior.optimisticAt;
        }
        if (prior?.chat) {
            chat = toWidgetChat(prior.chat);
        }
        if (prior?.meetings) {
            meetings = toWidgetMeetings(prior.meetings);
        }
        if (prior?.calls) {
            calls = toWidgetCalls(prior.calls);
        }
    } else {
        // Server answer is authoritative when it is newer than a widget tap's optimistic state.
        const prior = await readPriorSnapshot();
        const picked = pickAuthoritativeClocks(prior, { workday, task, clocksAt });

        workday = picked.workday;
        task = picked.task;
        clocksAt = picked.clocksAt;
        optimisticAt = picked.optimisticAt;
    }

    // Today's tasks (tracker widget list). OMITTED on a server that predates `listDailyTasks` (the widget then hides
    // the block); a transient failure carries the last published rows over instead of blanking a good list.
    let dailyTasks;
    if (daily?.ok) {
        dailyTasks = toWidgetDailyTasks(daily.tasks);
    } else if (!daily?.unsupported) {
        const prior = await readPriorSnapshot();

        dailyTasks = Array.isArray(prior?.dailyTasks) ? prior.dailyTasks.slice(0, DAILY_TASKS_WIDGET_MAX) : undefined;
    }

    return {
        generatedAt: new Date().toISOString(),
        sections: prefs.sections,
        restaurantId,
        calendar,
        tasks,
        mail: {
            unread: Number(summary?.mail?.unread || 0),
            // Tri-state on purpose: only an EXPLICIT `false` renders the widget's
            // "set up your mailbox" state — an older backend that never sends the
            // flag keeps the plain empty state (backward compatible).
            configured: typeof summary?.mail?.configured === 'boolean' ? summary.mail.configured : null,
            items: mailItems,
        },
        stats: {
            kpis,
            series,
            chartImageLight: null,
            chartImageDark: null,
        },
        timer,
        orders: { pending: pendingOrders },
        trackables,
        ...(dailyTasks ? { dailyTasks } : {}),
        chat,
        meetings,
        calls,
        workday,
        task,
        clocksAt,
        ...(optimisticAt ? { optimisticAt } : {}),
        quickModules: buildQuickModules(),
        insights,
        // Localized strings for the NATIVE widgets (contract C7) — they cannot
        // read the JS i18n catalog, so the app resolves them into the snapshot.
        labels: {
            timeTracker: buildTimeTrackerLabels(),
            mail: buildMailLabels(),
            widgets: buildCommonWidgetLabels(),
        },
    };
};

export default buildWidgetSnapshot;
